import React, { memo, useMemo, useState, useCallback, useEffect, lazy, Suspense } from 'react'
import { useAvatarUpload } from '../hooks'
import { MAX_AVATAR_FILE_SIZE } from '../hooks/useAvatarUpload'
import styles from './AvatarUpload.module.css'

// 懒加载裁剪弹窗：react-image-crop 及其 CSS 移出首屏 bundle，选择图片后才加载
const AvatarCropModal = lazy(() => import('./AvatarCropModal'))

// ============================================================================
// Types
// ============================================================================

export interface AvatarUploadProps {
  /** 当前头像 URL */
  currentAvatar?: string
  /** 用户名（用于生成默认头像） */
  userName?: string
  /** 头像尺寸 */
  size?: 'sm' | 'md' | 'lg'
  /** 上传成功回调 */
  onUploadSuccess: (avatarUrl: string) => void
  /** 上传失败回调 */
  onUploadError?: (error: string) => void
  /** 自定义类名 */
  className?: string
}

// ============================================================================
// Sub-components
// ============================================================================

interface AvatarImageProps {
  src?: string
  alt: string
  size: 'sm' | 'md' | 'lg'
}

const AvatarImage = memo<AvatarImageProps>(function AvatarImage({
  src,
  alt,
  size,
}) {
  const sizeClass = styles[`image--${size}`]
  const [error, setError] = useState(false)

  useEffect(() => {
    setError(false)
  }, [src])

  if (src && !error) {
    return (
      <img
        src={src}
        alt={alt}
        className={`${styles.image} ${sizeClass}`}
        loading="lazy"
        onError={(e) => {
          console.error('[AvatarImage] 图片加载失败:', src, e)
          setError(true)
        }}
      />
    )
  }

  return <div className={`${styles.placeholder} ${sizeClass}`}>{alt[0]}</div>
})

interface ProgressRingProps {
  progress: number
  size?: number
}

const ProgressRing = memo<ProgressRingProps>(function ProgressRing({
  progress,
  size = 60,
}) {
  const strokeWidth = 4
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (progress / 100) * circumference

  return (
    <div className={styles.progress} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={styles.progressSvg}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>
      <span className={styles.progressText}>{progress}%</span>
    </div>
  )
})

interface UploadOverlayProps {
  isUploading: boolean
  progress: number
}

const UploadOverlay = memo<UploadOverlayProps>(function UploadOverlay({
  isUploading,
  progress,
}) {
  if (!isUploading) return null

  return (
    <div className={styles.overlay}>
      <ProgressRing progress={progress} />
    </div>
  )
})

interface HoverOverlayProps {
  isVisible: boolean
}

const HoverOverlay = memo<HoverOverlayProps>(function HoverOverlay({
  isVisible,
}) {
  if (!isVisible) return null

  return (
    <div className={styles.hoverOverlay}>
      <svg
        className={styles.cameraIcon}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      <span className={styles.hoverText}>更换头像</span>
    </div>
  )
})

// ============================================================================
// Main Component
// ============================================================================

export const AvatarUpload = memo<AvatarUploadProps>(function AvatarUpload({
  currentAvatar,
  userName,
  size = 'md',
  onUploadSuccess,
  onUploadError,
  className,
}) {
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const { isUploading, progress, fileInputRef, openFilePicker, handleFileChange } =
    useAvatarUpload({
      onSuccess: onUploadSuccess,
      onError: onUploadError,
    })

  const displayName = useMemo(() => {
    const name = userName || '?'
    return name[0].toUpperCase()
  }, [userName])

  const sizeClass = styles[`button--${size}`]

  // 验证文件
  const validateFile = useCallback((file: File): string | null => {
    const ALLOWED_TYPES = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ]
    // ★ 复用共享限制常量，避免与 useAvatarUpload 重复不一致
    const MAX_FILE_SIZE = MAX_AVATAR_FILE_SIZE

    if (!ALLOWED_TYPES.includes(file.type)) {
      return '请选择图片文件 (JPG, PNG, WebP)'
    }
    if (file.size > MAX_FILE_SIZE) {
      return '图片大小不能超过 10MB'
    }
    return null
  }, [])

  // 处理文件选择 - 打开裁剪弹窗
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) return

    const validationError = validateFile(file)
    if (validationError) {
      onUploadError?.(validationError)
      // 重置文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // 保存选中的文件并打开裁剪弹窗
    setSelectedFile(file)
    setCropModalOpen(true)

    // 重置文件输入，允许再次选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [validateFile, onUploadError, fileInputRef])

  // 处理裁剪确认
  const handleCropConfirm = useCallback((croppedFile: File) => {
    setCropModalOpen(false)
    setSelectedFile(null)
    // 上传裁剪后的图片
    handleFileChange(croppedFile)
  }, [handleFileChange])

  // 处理裁剪取消
  const handleCropCancel = useCallback(() => {
    setCropModalOpen(false)
    setSelectedFile(null)
  }, [])

  return (
    <>
      <div className={`${styles.container} ${className || ''}`}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileInputChange}
          className={styles.input}
          aria-label="选择头像图片"
        />

        {/* Avatar button */}
        <button
          type="button"
          className={`${styles.button} ${sizeClass} ${
            isUploading ? styles['button--uploading'] : ''
          }`}
          onClick={openFilePicker}
          disabled={isUploading}
          aria-label="点击更换头像"
        >
          <div className={styles.preview}>
            <AvatarImage
              src={currentAvatar}
              alt={displayName}
              size={size}
            />
            <HoverOverlay isVisible={!isUploading} />
            <UploadOverlay isUploading={isUploading} progress={progress} />
          </div>
        </button>

        {/* Hint text */}
        <div className={styles.info}>
          <p className={styles.title}>点击更换头像</p>
          <p className={styles.hint}>支持 JPG、PNG、WebP，最大 10MB，5分钟同步</p>
        </div>
      </div>

      {/* 裁剪弹窗 */}
      {selectedFile && (
        <Suspense fallback={null}>
          <AvatarCropModal
            imageFile={selectedFile}
            isOpen={cropModalOpen}
            onConfirm={handleCropConfirm}
            onCancel={handleCropCancel}
          />
        </Suspense>
      )}
    </>
  )
})

export default AvatarUpload
